import type React from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../redux/hooks";
import { css } from "../../../styled-system/css";
import { HStack, Stack } from "../../../styled-system/jsx";
import getMetricSpec, { getFormatter } from "../merchandising/utils/metrics";
import type { TSelector } from "../../i18n/types";
import type { StageAggStats, Stage } from "./types";
import { getTrendColor } from "../../components/custom/summaryReport/utils";
import { formatPercent } from "../../utils/formatNumber";
import { HoverInfo } from "../../components/core/basic/Tooltip";

interface CardMetric {
  title: TSelector;
  valueAccessor: string;
  prevValueAccessor?: string;
}

const cardStats: CardMetric[] = [
  {
    title: ($) => $.query_funnel.cards["# of Queries"],
    valueAccessor: "count",
  },
  {
    title: ($) => $.query_funnel.cards["Ad Sales"],
    valueAccessor: "ad_sales",
    prevValueAccessor: "ad_sales_percent",
  },
  {
    title: ($) => $.query_funnel.cards["Ad Spend"],
    valueAccessor: "ad_spend",
    prevValueAccessor: "ad_spend_percent",
  },
  {
    title: ($) => $.query_funnel.cards.ACOS,
    valueAccessor: "acos",
    prevValueAccessor: "acos_delta",
  },
  {
    title: ($) => $.query_funnel.cards.CTR,
    valueAccessor: "ctr",
    prevValueAccessor: "click_thru_rate_delta",
  },
  {
    title: ($) => $.query_funnel.cards.Clicks,
    valueAccessor: "clicks",
    prevValueAccessor: "clicks_percent",
  },
  {
    title: ($) => $.query_funnel.cards.Impressions,
    valueAccessor: "impressions",
    prevValueAccessor: "impressions_percent",
  },
];

export const STAGE_LABEL_SELECTORS: Record<Stage, TSelector> = {
  awareness: ($) => $.query_funnel.stage_labels.awareness,
  consideration: ($) => $.query_funnel.stage_labels.consideration,
  conversion: ($) => $.query_funnel.stage_labels.conversion,
  loyalty: ($) => $.query_funnel.stage_labels.loyalty,
  unknown: ($) => $.query_funnel.stage_labels.unknown,
};

const Card: React.FC<CardProps> = ({ name, title, data, toolTip, cardMetrics, selected, setSelected }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["query_funnel"]);
  const stageSelector = (STAGE_LABEL_SELECTORS as Record<string, TSelector | undefined>)[title];
  const stageLabel = stageSelector ? t(stageSelector) : title.charAt(0).toUpperCase() + title.slice(1);
  return (
    <div
      className={css({
        cursor: "pointer",
        borderRadius: "25px",
        height: "150px",
        marginEnd: "4",
        marginBottom: "3",
        padding: "4",
        backgroundColor: selected?.value === title ? "rgb(15,195,152, 0.15)" : undefined,
        boxShadow: "none",
      })}
      style={{ border: `2px solid ${selected?.value === title ? "rgb(15,195,152, 0.50)" : "rgb(238, 238, 238)"}` }}
      onClick={() => {
        setSelected(selected?.value === title ? { label: "All Queries", value: "all" } : { label: name, value: title });
      }}
    >
      <HStack>
        <Stack>
          <span className={css({ fontSize: "3xl", fontWeight: "bold" })}>
            {stageLabel}
            {toolTip && <HoverInfo>{toolTip}</HoverInfo>}
          </span>
        </Stack>
      </HStack>
      <HStack className={css({ display: "flex", alignItems: "center", columnGap: "5rem", height: "100%" })}>
        {cardMetrics.map((metric) => {
          const { title: metricTitle, valueAccessor, prevValueAccessor } = metric;
          const metricLabel = t(metricTitle);

          const metricSpec = getMetricSpec(valueAccessor);
          const metricFormatter = getFormatter(marketPlace, metricSpec.format, metricSpec);
          const accessor = valueAccessor as keyof StageAggStats;
          const value = valueAccessor ? metricFormatter(data[accessor]) : null;
          const prevValue = prevValueAccessor ? data[prevValueAccessor as keyof StageAggStats] : null;
          const prevValueStyle = {
            color: getTrendColor(prevValue, valueAccessor, "rgb(15, 195, 152)", "rgb(240, 60, 103)"),
            fontWeight: "bold",
          };
          return (
            <Stack
              key={metricLabel}
              className={css({ alignItems: "flex-start", fontSize: "larger", textAlign: "center" })}
            >
              <span style={{ whiteSpace: "nowrap" }}>{metricLabel}</span>
              <span className={css({ display: "block", fontSize: "2xl", fontWeight: "bold" })}>{value}</span>
              {prevValue !== undefined && prevValue !== null && (
                <span style={prevValueStyle}>
                  {formatPercent(prevValue, {
                    decimalPlaces: 1,
                    addPlus: true,
                  })}
                </span>
              )}
            </Stack>
          );
        })}
      </HStack>
    </div>
  );
};

interface QueryStatsCardsProps {
  AggStats: Record<string, StageAggStats> | null;
  selectedStage: { label: string; value: string } | null;
  setSelectedStage: (stage: { label: string; value: string } | null) => void;
}
interface CardProps {
  name: string;
  title: string;
  data: StageAggStats;
  toolTip?: string;
  cardMetrics: CardMetric[];
  selected: { label: string; value: string } | null;
  setSelected: (stage: { label: string; value: string } | null) => void;
}

const QueryStatsCards: React.FC<QueryStatsCardsProps> = ({ AggStats, selectedStage, setSelectedStage }) => {
  const { t } = useTranslation(["query_funnel"]);
  return (
    <>
      {AggStats && Object.keys(AggStats).length > 0 ?
        Object.entries(AggStats).map(([stage, stats]) => (
          <Card
            key={stage}
            cardMetrics={cardStats}
            data={stats}
            title={stage}
            name={stage}
            selected={selectedStage}
            setSelected={setSelectedStage}
          />
        ))
      : <p>{t(($) => $.query_funnel.cards["No aggregate stats available"])}</p>}
    </>
  );
};

export default QueryStatsCards;
